import { formatDistanceToNow, parseISO, isPast } from 'date-fns';
import type { Milestone } from '@/types/schedule';
import styles from '@/styles/components/TimelineCard.module.css';

type MilestoneStatus = 'completed' | 'next' | 'upcoming' | 'goal';

interface TimelineCardProps {
  milestone: Milestone;
  status: MilestoneStatus;
  onMarkComplete?: (id: string) => void;
}

const STATUS_ICONS: Record<MilestoneStatus, string> = {
  completed: '✓',
  next: '→',
  upcoming: '⏰',
  goal: '🎯',
};

const BADGE_CLASSES: Record<MilestoneStatus, string> = {
  completed: styles.badgeCompleted,
  next: styles.badgeNext,
  upcoming: styles.badgeUpcoming,
  goal: styles.badgeGoal,
};

function getRelativeTimeLabel(isoTime: string): string {
  const date = parseISO(isoTime);
  if (isPast(date)) {
    return formatDistanceToNow(date, { addSuffix: true });
  }
  const distance = formatDistanceToNow(date);
  return `IN ${distance.toUpperCase()}`;
}

function getBadgeLabel(status: MilestoneStatus): string {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'next':
      return 'Next Up';
    case 'upcoming':
      return 'Upcoming';
    case 'goal':
      return 'Goal';
  }
}

export function TimelineCard({ milestone, status, onMarkComplete }: TimelineCardProps) {
  const timeLabel = getRelativeTimeLabel(milestone.time);

  return (
    <div className={styles.card} role="article" aria-label={`${milestone.name}: ${getBadgeLabel(status)}`}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <h3 className={styles.name}>{milestone.name}</h3>
          <p className={styles.time}>{timeLabel}</p>
        </div>
        <span className={styles.statusIcon} aria-hidden="true">
          {STATUS_ICONS[status]}
        </span>
      </div>

      {milestone.description && (
        <p className={styles.description}>{milestone.description}</p>
      )}

      <span className={`${styles.badge} ${BADGE_CLASSES[status]}`}>
        {STATUS_ICONS[status]} {getBadgeLabel(status)}
      </span>

      {status === 'next' && onMarkComplete && (
        <button
          className={styles.completeButton}
          onClick={() => onMarkComplete(milestone.id)}
          aria-label={`Mark ${milestone.name} as complete`}
        >
          ✓ Mark Complete
        </button>
      )}
    </div>
  );
}
